import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { Loading } from '../components/Loading'
import { StatusBadge } from '../components/StatusBadge'
import {
  MAX_UPLOAD_BYTES,
  MAX_UPLOAD_LABEL,
  TERMS_VERSION,
} from '../lib/constants'
import {
  formatArxivIdentifier,
  formatBytes,
  formatDate,
  getArxivAbstractUrl,
  hasPdfSignature,
  normalizeArxivIdentifier,
} from '../lib/utils'
import type { Submission, SubmissionSource } from '../types'

interface AcceptanceState {
  authorship: boolean
  rights: boolean
  limitedPermission: boolean
  firstLookNature: boolean
  sourceHandling: boolean
  terms: boolean
}

const EMPTY_ACCEPTANCE: AcceptanceState = {
  authorship: false,
  rights: false,
  limitedPermission: false,
  firstLookNature: false,
  sourceHandling: false,
  terms: false,
}

export function DashboardPage() {
  const { user } = useAuth()
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [sourceType, setSourceType] = useState<SubmissionSource>('pdf')
  const [file, setFile] = useState<File | null>(null)
  const [arxivInput, setArxivInput] = useState('')
  const [acceptance, setAcceptance] = useState<AcceptanceState>(EMPTY_ACCEPTANCE)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null)

  const loadSubmissions = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setLoadError(null)

    const { data, error } = await supabase
      .from('submissions')
      .select('*, feedbacks(*)')
      .eq('user_id', user.id)
      .order('submitted_at', { ascending: false })

    if (error) setLoadError(error.message)
    else setSubmissions((data ?? []) as Submission[])
    setLoading(false)
  }, [user])

  useEffect(() => {
    void loadSubmissions()
  }, [loadSubmissions])

  const activeSubmission = useMemo(
    () => submissions.find((item) => ['queued', 'in_progress'].includes(item.status)),
    [submissions],
  )

  const normalizedArxivId = useMemo(
    () => normalizeArxivIdentifier(arxivInput),
    [arxivInput],
  )

  const allAccepted = Object.values(acceptance).every(Boolean)
  const sourceIsReady = sourceType === 'pdf' ? Boolean(file) : Boolean(normalizedArxivId)

  const setAcceptanceItem = (key: keyof AcceptanceState, checked: boolean) => {
    setAcceptance((current) => ({ ...current, [key]: checked }))
  }

  const changeSourceType = (nextSource: SubmissionSource) => {
    setSourceType(nextSource)
    setUploadError(null)
    setUploadSuccess(null)
    setAcceptance(EMPTY_ACCEPTANCE)
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!user) return

    setUploadError(null)
    setUploadSuccess(null)

    if (activeSubmission) {
      setUploadError('You already have an active submission.')
      return
    }
    if (!allAccepted) {
      setUploadError('Please confirm every required declaration.')
      return
    }

    if (sourceType === 'pdf') {
      if (!file) {
        setUploadError('Choose a PDF file.')
        return
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        setUploadError(`The PDF exceeds the ${MAX_UPLOAD_LABEL} limit.`)
        return
      }
      if (!file.name.toLowerCase().endsWith('.pdf') || file.type !== 'application/pdf') {
        setUploadError('Only PDF files are accepted.')
        return
      }
      if (!(await hasPdfSignature(file))) {
        setUploadError('The selected file does not appear to be a valid PDF.')
        return
      }
    } else if (!normalizedArxivId) {
      setUploadError('Enter a valid arXiv identifier, for example arXiv:2603.12067.')
      return
    }

    setUploading(true)
    const submissionId = crypto.randomUUID()

    try {
      if (sourceType === 'arxiv') {
        const acceptancePayload = {
          ...acceptance,
          sourceType,
          arxivId: normalizedArxivId,
          submittedAtClient: new Date().toISOString(),
        }

        const { error: databaseError } = await supabase.rpc('create_arxiv_submission', {
          p_id: submissionId,
          p_arxiv_id: normalizedArxivId,
          p_terms_version: TERMS_VERSION,
          p_acceptance_payload: acceptancePayload,
        })

        if (databaseError) {
          setUploadError(databaseError.message)
          return
        }
      } else {
        if (!file) return

        const storagePath = `${user.id}/${submissionId}.pdf`
        const acceptancePayload = {
          ...acceptance,
          sourceType,
          submittedFileName: file.name,
          submittedFileSize: file.size,
          submittedAtClient: new Date().toISOString(),
        }

        const { error: databaseError } = await supabase.rpc('create_submission', {
          p_id: submissionId,
          p_storage_path: storagePath,
          p_original_filename: file.name,
          p_file_size_bytes: file.size,
          // Retained for compatibility with the existing database function.
          p_is_anonymous: false,
          p_terms_version: TERMS_VERSION,
          p_acceptance_payload: acceptancePayload,
        })

        if (databaseError) {
          setUploadError(databaseError.message)
          return
        }

        const { error: storageError } = await supabase.storage
          .from('manuscripts')
          .upload(storagePath, file, {
            cacheControl: '3600',
            contentType: 'application/pdf',
            upsert: false,
          })

        if (storageError) {
          await supabase.rpc('cancel_failed_submission', {
            p_submission_id: submissionId,
          })
          setUploadError(storageError.message)
          return
        }
      }

      setFile(null)
      setArxivInput('')
      setSourceType('pdf')
      setAcceptance(EMPTY_ACCEPTANCE)
      setUploadSuccess('Your manuscript has entered the anonymous public queue.')
      const input = document.getElementById('manuscript') as HTMLInputElement | null
      if (input) input.value = ''
      await loadSubmissions()
    } finally {
      setUploading(false)
    }
  }

  const withdraw = async (submission: Submission) => {
    const source = submission.source_type ?? 'pdf'
    const message =
      source === 'pdf'
        ? 'Withdraw this submission? The uploaded PDF will be deleted and no feedback will be produced.'
        : 'Withdraw this submission? The stored arXiv reference will be removed and no feedback will be produced.'

    const confirmed = window.confirm(message)
    if (!confirmed) return

    const { error } = await supabase.functions.invoke('withdraw-submission', {
      body: { submissionId: submission.id },
    })
    if (error) {
      window.alert(error.message)
      return
    }
    await loadSubmissions()
  }

  return (
    <section className="section">
      <div className="container">
        {loadError && <div className="notice notice-error">{loadError}</div>}

        <div className="dashboard-grid">
          <div className="dashboard-main">
            <div className="panel">
              <div className="panel-header">
                <div>
                  <h2>Submission history</h2>
                  <p>
                    A replacement private PDF or a different arXiv work requires a new
                    submission.
                  </p>
                </div>
              </div>

              {loading ? (
                <Loading label="Loading your submissions..." />
              ) : submissions.length === 0 ? (
                <p className="empty-state">You have not submitted a manuscript yet.</p>
              ) : (
                <div className="submission-list">
                  {submissions.map((submission) => {
                    const source = submission.source_type ?? 'pdf'
                    const hasPrivateResponse = [
                      'completed',
                      'unreadable',
                      'declined_conflict',
                      'rejected_security',
                    ].includes(submission.status)

                    return (
                      <article className="submission-card" key={submission.id}>
                        <div className="submission-card-top">
                          <div>
                            <span className="mono submission-code">
                              {submission.public_code}
                            </span>
                            <p>
                              Submitted {formatDate(submission.submitted_at)}
                              {source === 'pdf' && submission.file_size_bytes
                                ? ` - ${formatBytes(submission.file_size_bytes)}`
                                : ''}
                            </p>
                          </div>
                          <StatusBadge status={submission.status} />
                        </div>

                        <dl className="detail-grid">
                          <div>
                            <dt>Source</dt>
                            <dd>
                              {source === 'arxiv' ? (
                                submission.arxiv_id ? (
                                  <a
                                    href={getArxivAbstractUrl(submission.arxiv_id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {formatArxivIdentifier(submission.arxiv_id)}
                                  </a>
                                ) : (
                                  'arXiv reference removed'
                                )
                              ) : (
                                'Private PDF'
                              )}
                            </dd>
                          </div>
                          <div>
                            <dt>Terms version</dt>
                            <dd>{submission.terms_version}</dd>
                          </div>
                        </dl>

                        <div className="card-actions">
                          {source === 'arxiv' && submission.arxiv_id && (
                            <a
                              className="button button-secondary button-small"
                              href={getArxivAbstractUrl(submission.arxiv_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open on arXiv
                            </a>
                          )}
                          {hasPrivateResponse && (
                            <Link
                              className="button button-primary button-small"
                              to={`/feedback/${submission.id}`}
                            >
                              View and download private feedback
                            </Link>
                          )}
                          {submission.status === 'queued' && (
                            <button
                              className="button button-danger-quiet button-small"
                              type="button"
                              onClick={() => void withdraw(submission)}
                            >
                              {source === 'pdf'
                                ? 'Withdraw and delete PDF'
                                : 'Withdraw submission'}
                            </button>
                          )}
                        </div>
                      </article>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          <aside className="dashboard-side">
            <div className="panel sticky-panel">
              <h2>Submit a manuscript</h2>
              {activeSubmission ? (
                <div className="notice notice-neutral">
                  You can have only one active submission. Submit another manuscript after
                  the current one is completed or withdrawn.
                </div>
              ) : (
                <form className="stack-form upload-form" onSubmit={submit}>
                  <fieldset>
                    <legend>Manuscript source</legend>
                    <div className="source-options">
                      <label
                        className={`source-option${
                          sourceType === 'pdf' ? ' source-option-active' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="source-type"
                          value="pdf"
                          checked={sourceType === 'pdf'}
                          onChange={() => changeSourceType('pdf')}
                        />
                        <span>
                          <strong>Upload a private PDF</strong>
                          <small>Stored temporarily in private platform storage.</small>
                        </span>
                      </label>

                      <label
                        className={`source-option${
                          sourceType === 'arxiv' ? ' source-option-active' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="source-type"
                          value="arxiv"
                          checked={sourceType === 'arxiv'}
                          onChange={() => changeSourceType('arxiv')}
                        />
                        <span>
                          <strong>Provide an arXiv identifier</strong>
                          <small>The manuscript will be opened directly on arXiv.</small>
                        </span>
                      </label>
                    </div>
                  </fieldset>

                  {sourceType === 'pdf' ? (
                    <div>
                      <input
                        id="manuscript"
                        type="file"
                        accept="application/pdf,.pdf"
                        required
                        hidden
                        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                      />
                      <div className="card-actions file-picker-row">
                        <label
                          className="button button-secondary button-small"
                          htmlFor="manuscript"
                        >
                          Choose file
                        </label>
                        <small>{file ? file.name : 'No file selected'}</small>
                      </div>
                      <small>Maximum {MAX_UPLOAD_LABEL}.</small>
                      <small>You may upload either a signed or an anonymized PDF.</small>
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="arxiv-identifier">arXiv identifier</label>
                      <input
                        id="arxiv-identifier"
                        type="text"
                        value={arxivInput}
                        onChange={(event) => setArxivInput(event.target.value)}
                        placeholder="arXiv:2603.12067"
                        autoComplete="off"
                        required
                      />
                      <small>Example: arXiv:2603.12067.</small>
                      <small>
                        Do not specify v1, v2, or another version. The latest version
                        available on arXiv at the time of reading will be used.
                      </small>
                      {arxivInput && normalizedArxivId && (
                        <small className="arxiv-valid">
                          Recognized as {formatArxivIdentifier(normalizedArxivId)}.
                        </small>
                      )}
                      {arxivInput && !normalizedArxivId && (
                        <small className="arxiv-invalid">
                          Use a valid identifier, for example arXiv:2603.12067.
                        </small>
                      )}
                    </div>
                  )}

                  <fieldset>
                    <legend>Required declarations</legend>
                    <label className="check-row">
                      <input
                        type="checkbox"
                        checked={acceptance.authorship}
                        onChange={(event) =>
                          setAcceptanceItem('authorship', event.target.checked)
                        }
                      />
                      <span>
                        I am an author or coauthor and I am authorized by the other authors
                        and relevant parties to submit this work.
                      </span>
                    </label>
                    <label className="check-row">
                      <input
                        type="checkbox"
                        checked={acceptance.rights}
                        onChange={(event) => setAcceptanceItem('rights', event.target.checked)}
                      />
                      <span>
                        {sourceType === 'pdf'
                          ? 'I have the right to share every included text, image, dataset, and other element, and I am not uploading prohibited sensitive data.'
                          : 'The identifier refers to my publicly available arXiv work, and I am permitted to submit it for this service.'}
                      </span>
                    </label>
                    <label className="check-row">
                      <input
                        type="checkbox"
                        checked={acceptance.limitedPermission}
                        onChange={(event) =>
                          setAcceptanceItem('limitedPermission', event.target.checked)
                        }
                      />
                      <span>
                        {sourceType === 'pdf'
                          ? 'I grant only the temporary permission needed to receive, store, open, and read this PDF for the private feedback.'
                          : 'I authorize the operator to open and read the public manuscript directly on arXiv for the private feedback.'}
                      </span>
                    </label>
                    <label className="check-row">
                      <input
                        type="checkbox"
                        checked={acceptance.firstLookNature}
                        onChange={(event) =>
                          setAcceptanceItem('firstLookNature', event.target.checked)
                        }
                      />
                      <span>
                        I understand this is not peer review, certification, or an editorial
                        assessment.
                      </span>
                    </label>
                    <label className="check-row">
                      <input
                        type="checkbox"
                        checked={acceptance.sourceHandling}
                        onChange={(event) =>
                          setAcceptanceItem('sourceHandling', event.target.checked)
                        }
                      />
                      <span>
                        {sourceType === 'pdf'
                          ? 'I understand that the source PDF is deleted after the feedback is published, while the private feedback stays in my active account.'
                          : 'I understand that Rapid First Look stores only the arXiv identifier, does not store a manuscript copy, and will read the latest version available at the time of reading.'}
                      </span>
                    </label>
                    <label className="check-row">
                      <input
                        type="checkbox"
                        checked={acceptance.terms}
                        onChange={(event) => setAcceptanceItem('terms', event.target.checked)}
                      />
                      <span>
                        I accept the <Link to="/terms">Terms of service</Link>, version{' '}
                        {TERMS_VERSION}, and acknowledge the{' '}
                        <Link to="/privacy">Privacy notice</Link>.
                      </span>
                    </label>
                  </fieldset>

                  <button
                    className="button button-primary"
                    type="submit"
                    disabled={uploading || !sourceIsReady || !allAccepted}
                  >
                    {uploading
                      ? sourceType === 'pdf'
                        ? 'Uploading securely...'
                        : 'Submitting...'
                      : 'Submit manuscript to the queue'}
                  </button>
                </form>
              )}

              {uploadError && <div className="notice notice-error">{uploadError}</div>}
              {uploadSuccess && <div className="notice notice-success">{uploadSuccess}</div>}
            </div>
          </aside>
        </div>
      </div>
    </section>
  )
}
